import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, ArrowLeft } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tm = ({ name }) => (
  <span>{name}<sup className="align-super text-[0.5em]">™</sup></span>
);

const TIERS = [
  {
    id: "mini_sample_kit",
    name: "Mini Sample Kit",
    fallbackPrice: "$29 USD",
    description: "Essential substrate swatches and tactile reference material for foundational testing.",
    includes: ["Core diffusion swatches", "Technical spec sheet", "Global standard tracked delivery"],
    highlight: false,
  },
  {
    id: "refined_demonstration",
    name: "Refined Demonstration",
    badge: "ARCHIVAL STANDARD",
    fallbackPrice: "$75 USD",
    description: "The comprehensive tactile manifestation of the Pixel Series™ formulation.",
    includes: [
      "Letter-size production-grade rear-projection diffusion material",
      "Complete material index",
      "Priority global tracking",
      "$75 credit toward future ecosystem integration",
    ],
    highlight: true,
  },
];

let _stripePromise = null;
async function resolveStripe() {
  if (_stripePromise) return _stripePromise;
  const res = await fetch(`${API}/payments/config`);
  const { publishable_key } = await res.json();
  _stripePromise = loadStripe(publishable_key);
  return _stripePromise;
}

function EmbeddedView({ tier, onBack }) {
  const [stripeP, setStripeP] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    resolveStripe()
      .then((p) => active && setStripeP(p))
      .catch(() => active && setError("Secure checkout could not be reached."));
    return () => {
      active = false;
    };
  }, []);

  const fetchClientSecret = useCallback(
    () =>
      fetch(`${API}/payments/checkout/embedded`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lookup_key: tier.id, origin_url: window.location.origin }),
      })
        .then((r) => {
          if (!r.ok) throw new Error("checkout");
          return r.json();
        })
        .then((d) => d.client_secret),
    [tier.id],
  );

  return (
    <div data-testid="embedded-checkout-view">
      <button
        type="button"
        data-testid="embedded-checkout-back"
        onClick={onBack}
        className="font-mono-spec mb-7 inline-flex items-center gap-2 text-[0.68rem] text-[#F5F5F7]/55 transition-colors duration-300 hover:text-[#84DFF4]"
      >
        <ArrowLeft size={13} /> Reconsider the artifact
      </button>
      <p className="font-mono-spec mb-2 text-[0.62rem] text-[#8E8E93]/80">Acquisition — {tier.name}</p>
      {error ? (
        <p data-testid="embedded-checkout-error" className="mt-6 text-sm font-light text-red-400">{error}</p>
      ) : !stripeP ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-[#84DFF4]" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-white">
          <EmbeddedCheckoutProvider stripe={stripeP} options={{ fetchClientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      )}
    </div>
  );
}

export default function SampleAcquire() {
  const [prices, setPrices] = useState({});
  const [selected, setSelected] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/payments/catalog`)
      .then((r) => r.json())
      .then(setPrices)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [selected]);

  if (selected) {
    return (
      <div ref={scrollRef} className="w-full">
        <EmbeddedView tier={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div data-testid="sample-acquire" className="w-full">
      <div className="text-center">
        <p className="font-mono-spec text-[0.62rem] text-[#8E8E93]/80">Tactile Verification</p>
        <h2 className="font-display mt-5 text-3xl font-light tracking-tight text-[#F5F5F7] sm:text-4xl">
          The Transfer of a Rare Artifact
        </h2>
        <p className="mx-auto mt-5 max-w-md text-sm font-light leading-[1.7] text-[#F5F5F7]/60">
          Digital vision requires physical truth. Request a direct material specimen from the{" "}
          <Tm name="PrismPixel" /> archive, dispatched under tracked global custody.
        </p>
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-2">
        {TIERS.map((tier) => {
          const live = prices[tier.id];
          const priceLabel = live ? live.formatted : tier.fallbackPrice;
          return (
            <div
              key={tier.id}
              data-testid={`tier-${tier.id}`}
              className={`relative flex flex-col p-8 transition-colors duration-300 ${
                tier.highlight
                  ? "border border-[rgba(132,223,244,0.28)] shadow-[0_0_60px_-24px_rgba(132,223,244,0.4)]"
                  : "border border-[rgba(132,223,244,0.12)] hover:border-[rgba(132,223,244,0.22)]"
              }`}
              style={{ backgroundColor: "#030305" }}
            >
              {tier.badge && (
                <span className="font-mono-spec absolute -top-2.5 left-8 bg-[#030305] px-3 py-1 text-[0.58rem] text-[#8E8E93]">
                  {tier.badge}
                </span>
              )}
              <h3 className="font-display text-lg font-light text-[#F5F5F7]">{tier.name}</h3>
              <p
                data-testid={`tier-${tier.id}-price`}
                className="font-mono-spec mt-3 text-xl font-normal tracking-normal text-[#F5F5F7]"
              >
                {priceLabel}
              </p>
              <p className="mt-4 text-sm font-light leading-[1.7] text-[#F5F5F7]/55">
                {tier.id === "refined_demonstration" ? (
                  <>The comprehensive tactile manifestation of the <Tm name="Pixel Series" /> formulation, featuring{" "}
                  <Tm name="PrismPixel Glass" /> and <Tm name="PrismPixel Glow" /> on a refined substrate set.</>
                ) : (
                  tier.description
                )}
              </p>
              <ul className="mt-6 space-y-2.5">
                {tier.includes.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm font-light text-[#F5F5F7]/75">
                    <Check size={14} className="mt-1 shrink-0 text-[#84DFF4]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid={`acquire-${tier.id}`}
                onClick={() => setSelected(tier)}
                className={`font-display mt-8 w-full px-6 py-3.5 text-sm font-light tracking-[0.04em] transition-colors duration-300 ${
                  tier.highlight
                    ? "border border-[#84DFF4] bg-[#84DFF4] text-[#030305] hover:bg-[#D4AF37]"
                    : "border border-[#84DFF4]/40 text-[#F5F5F7] hover:border-[#84DFF4] hover:bg-[#84DFF4]/10"
                }`}
              >
                Acquire — {priceLabel}
              </button>
            </div>
          );
        })}
      </div>
      <p className="font-mono-spec mx-auto mt-8 max-w-sm text-center text-[0.58rem] leading-[1.9] text-[#F5F5F7]/35">
        Secure payment by Stripe · Completed within this view
      </p>
    </div>
  );
}
